import { lazy, Suspense, useState } from 'react';
import {
  createEnrolmentSession,
  submitEnrolmentResult,
  type CreatedSession,
  type Me,
} from '../../api/endpoints.js';
import type { ApiFailure } from '../../api/client.js';
import { useAction, useIntentKey } from '../../api/hooks.js';
import { goStep } from '../../flow/nav.js';
import { Failure, Loading } from '../../ui/kit.js';
import { FlowScreen, FlowSub, FlowTitle, PrimaryButton } from '../../ui/flow.js';

/**
 * Mobile screen 14 — the selfie. On this backend the selfie is a Rekognition
 * Face Liveness capture: the video streams from this browser straight to AWS
 * and never touches the api, and the verdict is read server-side by the result
 * route. Amplify UI + TensorFlow stay in their own lazy chunk, as before.
 */
const LivenessCapture = lazy(() => import('../liveness-capture.js'));

type Stage = 'idle' | 'capturing' | 'submitting' | 'spent';

const SUBMIT_HINTS: Record<string, string> = {
  LIVENESS_FAILED:
    'The provider judged the capture not live. That session is spent — start a new scan.',
  QUALITY_TOO_LOW: 'Below the quality floor — that session is spent, start a fresh scan.',
  IDENTITY_CONFLICT:
    'This face is already enrolled to another person. Not retryable — a human has to resolve it.',
  SESSION_CONSUMED: 'This session was already submitted — start a new scan.',
  SESSION_EXPIRED: 'The session timed out — start a new scan.',
};

const CREATE_HINTS: Record<string, string> = {
  CONSENT_NOT_SIGNED: 'Sign the biometric consent first — go back one step.',
  DOB_REQUIRED: 'A date of birth is required before a face can be enrolled.',
  UNDER_MIN_AGE: 'Below the enrolment age floor.',
  SESSION_IN_PROGRESS: 'A scan is already open for this person — wait for it to expire.',
  LIVENESS_NOT_READY: 'The face-scan service is not ready to take a session right now.',
  TOO_MANY_ATTEMPTS: 'The daily scan attempts for this person are used up — try again tomorrow.',
};

/**
 * The capture loop, shared by the self-selfie and the family-member selfie:
 * create a session, stream the check, submit the result, and treat every
 * failure as a SPENT session (creating one burns a provider attempt, so the
 * way back is a new scan, never a retry).
 */
export function EnrolmentCapture({
  subjectPersonId,
  startLabel,
  onEnrolled,
}: {
  subjectPersonId: string;
  startLabel: string;
  onEnrolled: () => void;
}) {
  const [stage, setStage] = useState<Stage>('idle');
  const [session, setSession] = useState<CreatedSession | null>(null);
  const [captureError, setCaptureError] = useState<ApiFailure | null>(null);
  const resultKey = useIntentKey();

  const submit = useAction(submitEnrolmentResult, () => {
    resultKey.next();
    onEnrolled();
  });

  const create = useAction(createEnrolmentSession, (created) => {
    setSession(created);
    setCaptureError(null);
    setStage('capturing');
  });

  if (stage === 'capturing' && session !== null) {
    return (
      <Suspense fallback={<Loading />}>
        <LivenessCapture
          session={session}
          onAnalysisComplete={() => {
            setStage('submitting');
            submit.run(session.session_id, resultKey.key);
          }}
          onFailure={(failure) => {
            setCaptureError(failure);
            setStage('spent');
          }}
          onCancel={() => {
            setCaptureError(null);
            setStage('spent');
          }}
        />
      </Suspense>
    );
  }

  return (
    <>
      {stage === 'submitting' && submit.error === null && (
        <p className="flow-verifying">Checking your scan…</p>
      )}
      {stage === 'spent' && captureError !== null && (
        <Failure
          error={captureError}
          hint="The scan did not complete, so nothing was enrolled. That session is spent — start a new scan."
        />
      )}
      {stage === 'spent' && captureError === null && (
        <p className="flow-note">
          The scan was abandoned before it completed. <strong>Nothing was enrolled.</strong> Start
          a new scan when you are ready.
        </p>
      )}
      {submit.error !== null && (
        <Failure error={submit.error} hint={SUBMIT_HINTS[submit.error.code]} />
      )}
      {create.error !== null && (
        <Failure error={create.error} hint={CREATE_HINTS[create.error.code]} />
      )}
      <PrimaryButton
        onClick={() => {
          setCaptureError(null);
          submit.reset();
          create.run(subjectPersonId);
        }}
        disabled={create.pending || (stage === 'submitting' && submit.pending)}
      >
        {create.pending
          ? 'Starting…'
          : stage === 'idle'
            ? startLabel
            : stage === 'submitting' && submit.pending
              ? 'Checking…'
              : 'Start a new scan'}
      </PrimaryButton>
    </>
  );
}

export function SelfieStep({ me, reloadMe }: { me: Me; reloadMe: () => void }) {
  return (
    <FlowScreen progress={86} onBack={() => window.history.back()}>
      <FlowTitle>Let&rsquo;s get your selfie</FlowTitle>
      <FlowSub>
        Position your face within the frame. The camera check streams securely to the face-scan
        service; no photo of you ever passes through ImageShield&rsquo;s own servers.
      </FlowSub>
      <EnrolmentCapture
        subjectPersonId={me.person.person_id}
        startLabel="Capture Selfie"
        onEnrolled={() => {
          reloadMe();
          goStep('profile-extra', { replace: true });
        }}
      />
    </FlowScreen>
  );
}
